import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Handshake, MapPin, Users, ChevronRight, Copy, ShieldAlert } from "lucide-react";
import { getContractorScope, listPartnerships, inviteContractorPartner } from "@/lib/contractors.functions";
import {
  PARTNERSHIP_STATUS_LABEL, canManageContractorPartners, type PartnershipStatus,
} from "@/lib/contractors";
import { ContractorWorkflowStrip } from "@/components/contractors/ContractorWorkflowStrip";
import { ContractorDisclaimer } from "@/components/contractors/ContractorDisclaimer";

export const Route = createFileRoute("/_authenticated/contractors/")({
  component: ContractorPartnersPage,
  head: () => ({
    meta: [
      { title: "Contractor Partners — MissionReady" },
      {
        name: "description",
        content:
          "Invite third-party contractor organizations, assign them to sites, publish contracted shifts and review pay periods.",
      },
      { property: "og:title", content: "Contractor Partners — MissionReady" },
      { property: "og:description", content: "Manage third-party contractor organizations attached to your sites." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function statusTone(status: string) {
  switch (status) {
    case "active":
      return "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
    case "accepted":
      return "border-sky-500/40 bg-sky-500/10 text-sky-300";
    case "invited":
      return "border-amber-500/40 bg-amber-500/10 text-amber-300";
    case "inactive":
      return "border-orange-500/40 bg-orange-500/10 text-orange-300";
    case "revoked":
      return "border-destructive/40 bg-destructive/10 text-destructive";
    default:
      return "border-border bg-muted/30 text-muted-foreground";
  }
}

function ContractorPartnersPage() {
  const { currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const orgId = currentOrg?.id ?? "";
  const qc = useQueryClient();
  const listFn = useServerFn(listPartnerships);
  const scopeFn = useServerFn(getContractorScope);
  const inviteFn = useServerFn(inviteContractorPartner);

  const allowed = canManageContractorPartners({ role: currentRole, isPlatformAdmin });

  const { data, isLoading, error } = useQuery({
    queryKey: ["contractor-partnerships", orgId],
    enabled: Boolean(orgId) && allowed,
    queryFn: () => listFn({ data: { orgId } }),
  });
  const { data: scope } = useQuery({
    queryKey: ["contractor-scope", orgId],
    enabled: Boolean(orgId) && allowed,
    queryFn: () => scopeFn({ data: { orgId } }),
  });
  const { data: sites } = useQuery({
    queryKey: ["contractor-assignable-sites", orgId],
    enabled: Boolean(orgId) && allowed,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sites")
        .select("id, name")
        .eq("organization_id", orgId)
        .eq("active", true)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const assignable = useMemo(() => {
    const all = sites ?? [];
    if (!scope || scope.orgWide) return all;
    return all.filter((s: any) => scope.scopedSiteIds.includes(s.id));
  }, [sites, scope]);

  const partnerships = (data?.partnerships ?? []) as any[];

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    contractorName: "",
    email: "",
    purpose: "",
    relationshipType: "temporary" as "temporary" | "ongoing",
    startsOn: "",
    endsOn: "",
    kind: "organization" as "organization" | "individual",
    siteIds: [] as string[],
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setBusy(true);
    try {
      const res: any = await inviteFn({
        data: {
          orgId,
          contractorName: form.contractorName.trim(),
          email: form.email.trim().toLowerCase(),
          purpose: form.purpose.trim() || undefined,
          relationshipType: form.relationshipType,
          startsOn: form.startsOn || undefined,
          endsOn: form.endsOn || undefined,
          siteIds: form.siteIds,
          kind: form.kind,
          origin: typeof window !== "undefined" ? window.location.origin : undefined,
        },
      });
      const link = `${window.location.origin}/accept-contract/${res.token}`;
      try { await navigator.clipboard.writeText(link); } catch { /* clipboard optional */ }
      if (res.emailSent) toast.success(`Invitation emailed to ${form.email.trim().toLowerCase()} — link also copied`);
      else toast.warning(`Invitation created, but the email couldn't be sent${res.emailError ? ` (${res.emailError})` : ""}. Link copied — share it directly.`);
      setOpen(false);
      setForm({ ...form, contractorName: "", email: "", purpose: "", siteIds: [] });
      await qc.invalidateQueries({ queryKey: ["contractor-partnerships", orgId] });
    } catch (err: any) {
      toast.error(typeof err?.message === "string" ? err.message : "Could not send the invitation");
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-xl border border-border bg-card p-6">
          <ShieldAlert className="h-6 w-6 text-muted-foreground" aria-hidden />
          <h1 className="mt-3 font-display text-xl font-semibold">Contractor Partners</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your role doesn't include managing contractor partner relationships.
          </p>
        </div>
      </div>
    );
  }

  const step = partnerships.length === 0 ? 0 : partnerships.some((p) => p.status === "invited") ? 1 : 4;

  return (
    <div className="mx-auto max-w-5xl px-4 pb-24 pt-4 sm:pt-6">
      <header className="mb-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Personnel</p>
        <h1 className="mt-1 font-display text-2xl font-semibold">Contractor Partners</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Third-party contractor organizations attached to your sites. Their personnel never become your
          employees and never use your paid seats.
        </p>
        {scope && !scope.orgWide ? (
          <p className="mt-2 rounded-md border border-border bg-muted/20 p-2 text-xs text-muted-foreground">
            You're managing contractor partners for your assigned site(s) only.
          </p>
        ) : null}
      </header>

      <ContractorWorkflowStrip currentStep={step} className="mb-5" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="mb-5 min-h-12 w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" aria-hidden /> Invite Contractor Partner
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Invite Contractor Partner</DialogTitle>
            <DialogDescription>
              Invite the contractor company's administrator, or a single independent contractor. Either
              way they stay in the contractor relationship — never in your personnel directory.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cp-kind">Invitation type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v as any })}>
                <SelectTrigger id="cp-kind" className="min-h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="organization">Contractor organization administrator</SelectItem>
                  <SelectItem value="individual">Individual contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-name">Contractor organization name</Label>
              <Input id="cp-name" required minLength={2} className="min-h-11" placeholder="ABC Security LLC"
                value={form.contractorName} onChange={(e) => setForm({ ...form, contractorName: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-email">Email</Label>
              <Input id="cp-email" type="email" required className="min-h-11" placeholder="owner@abcsecurity.com"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-purpose">Purpose of the relationship</Label>
              <Textarea id="cp-purpose" rows={2} placeholder="Overflow coverage for the north campus"
                value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="cp-type">Duration</Label>
                <Select value={form.relationshipType} onValueChange={(v) => setForm({ ...form, relationshipType: v as any })}>
                  <SelectTrigger id="cp-type" className="min-h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="temporary">Temporary</SelectItem>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cp-start">Start date</Label>
                <Input id="cp-start" type="date" className="min-h-11" value={form.startsOn}
                  onChange={(e) => setForm({ ...form, startsOn: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cp-end">End date</Label>
                <Input id="cp-end" type="date" className="min-h-11" value={form.endsOn}
                  onChange={(e) => setForm({ ...form, endsOn: e.target.value })} />
              </div>
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Site(s) to assign</legend>
              {assignable.length === 0 ? (
                <p className="text-xs text-muted-foreground">No sites available in your scope yet.</p>
              ) : (
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                  {assignable.map((s: any) => {
                    const checked = form.siteIds.includes(s.id);
                    return (
                      <label key={s.id} className="flex min-h-10 cursor-pointer items-center gap-3 rounded px-2 text-sm hover:bg-muted/40">
                        <input
                          type="checkbox"
                          className="h-5 w-5 accent-primary"
                          checked={checked}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              siteIds: e.target.checked
                                ? [...form.siteIds, s.id]
                                : form.siteIds.filter((id) => id !== s.id),
                            })
                          }
                        />
                        <span>{s.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </fieldset>
            <ContractorDisclaimer />
            <DialogFooter>
              <Button type="submit" className="min-h-12 w-full" disabled={busy}>
                {busy ? "Sending…" : "Send invitation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading contractor partners…</p>
      ) : error ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Data source unavailable. Pull to refresh or try again shortly.
        </p>
      ) : partnerships.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <Handshake className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
          <h2 className="mt-3 font-display text-lg font-semibold">No contractor partners yet</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Invite a contractor company to cover your sites. They set up their own organization, add their
            own personnel, and claim the shifts you publish.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {partnerships.map((p) => (
            <li key={p.id}>
              <Link
                to="/contractors/$partnershipId"
                params={{ partnershipId: p.id }}
                className="block rounded-xl border border-border bg-card p-4 transition hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate font-display text-lg font-semibold">{p.contractor_name}</h2>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {p.relationship_type === "ongoing" ? "Ongoing" : "Temporary"}
                      {p.starts_on ? ` · ${p.starts_on}` : ""}
                      {p.ends_on ? ` → ${p.ends_on}` : ""}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${statusTone(p.status)}`}>
                    {PARTNERSHIP_STATUS_LABEL[p.status as PartnershipStatus] ?? p.status}
                  </span>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                  <div className="rounded-md bg-muted/30 p-2">
                    <dt className="flex items-center gap-1 text-muted-foreground"><MapPin className="h-3 w-3" aria-hidden /> Sites</dt>
                    <dd className="mt-0.5 font-semibold">{p.sites.length}</dd>
                  </div>
                  <div className="rounded-md bg-muted/30 p-2">
                    <dt className="flex items-center gap-1 text-muted-foreground"><Users className="h-3 w-3" aria-hidden /> Members</dt>
                    <dd className="mt-0.5 font-semibold">{p.memberCount}</dd>
                  </div>
                  <div className="rounded-md bg-muted/30 p-2">
                    <dt className="text-muted-foreground">Unclaimed</dt>
                    <dd className="mt-0.5 font-semibold">{p.unclaimedCount}</dd>
                  </div>
                  <div className="rounded-md bg-muted/30 p-2">
                    <dt className="text-muted-foreground">Timesheet</dt>
                    <dd className="mt-0.5 font-semibold capitalize">
                      {p.latestPayPeriod ? String(p.latestPayPeriod.status).replace(/_/g, " ") : "—"}
                    </dd>
                  </div>
                </dl>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-primary">
                  Open relationship <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
